<template>
  <div class="transaction-list">
    <table class="data-table">
      <thead>
        <tr>
          <th>ID</th>
          <th>类型</th>
          <th>金额</th>
          <th>余额变化</th>
          <th>说明</th>
          <th>操作员</th>
          <th>状态</th>
          <th>流水号</th>
          <th>时间</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="item in list" :key="item.id">
          <td>{{ item.id }}</td>
          <td>{{ typeText(item.transaction_type) }}</td>
          <td :class="['amount', item.transaction_type === 1 || item.transaction_type === 4 || item.transaction_type === 7 ? 'plus' : 'minus']">
            {{ item.transaction_type === 1 || item.transaction_type === 4 || item.transaction_type === 7 ? '+' : '-' }}{{ (Math.abs(item.amount) / 100).toFixed(2) }}
          </td>
          <td>{{ (item.balance_before / 100).toFixed(2) }} → {{ (item.balance_after / 100).toFixed(2) }}</td>
          <td>{{ item.description || '-' }}</td>
          <td>
            <span v-if="item.operator_name">{{ item.operator_name }}</span>
            <span v-else-if="item.operator_id">ID:{{ item.operator_id }}</span>
            <span v-else class="muted">系统</span>
          </td>
          <td>
            <span :class="['status-tag', statusText(item.status).cls]">{{ statusText(item.status).text }}</span>
          </td>
          <td>{{ item.transaction_no || '-' }}</td>
          <td>{{ item.created_at }}</td>
        </tr>
        <tr v-if="list.length === 0">
          <td colspan="9" class="empty">暂无流水记录</td>
        </tr>
      </tbody>
    </table>

    <div class="pagination" v-if="total > 0">
      <button class="page-btn" :disabled="currentPage <= 1" @click="changePage(currentPage - 1)">上一页</button>
      <span class="page-info">第 {{ currentPage }} / {{ totalPages }} 页（共 {{ total }} 条）</span>
      <button class="page-btn" :disabled="currentPage >= totalPages" @click="changePage(currentPage + 1)">下一页</button>
    </div>
  </div>
</template>

<script>
import { getTransactions, getRunnerTransactions } from '@/api/finance'

export default {
  name: 'TransactionList',
  props: {
    userId: {
      type: [Number, String],
      default: null
    },
    // 传入 runnerId 时，按跑手独立钱包精确查询（role_code=runner），否则按原 user_id 口径
    runnerId: {
      type: [Number, String],
      default: null
    },
    // 传入 roleCode 时，在 user_id 查询结果上附加 role_code 过滤（如店家详情传 'shop'）
    roleCode: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      list: [],
      total: 0,
      currentPage: 1
    }
  },
  watch: {
    userId: {
      immediate: true,
      handler() {
        if (this.runnerId || this.roleCode) return
        this.currentPage = 1
        this.loadTransactions()
      }
    },
    runnerId: {
      immediate: true,
      handler() {
        if (this.runnerId) {
          this.currentPage = 1
          this.loadTransactions()
        }
      }
    },
    roleCode: {
      immediate: true,
      handler() {
        if (this.roleCode) {
          this.currentPage = 1
          this.loadTransactions()
        }
      }
    }
  },
  computed: {
    // 每页 20 条（与后端 paginate(20) 保持一致）
    totalPages() {
      return Math.ceil(this.total / 20) || 1
    }
  },
  methods: {
    async loadTransactions() {
      if (!this.userId && !this.runnerId && !this.roleCode) {
        this.list = []
        this.total = 0
        return
      }
      try {
        let res
        if (this.runnerId) {
          // 跑手独立钱包流水：精确对应 role_code=runner，与头部"余额"口径一致
          res = await getRunnerTransactions({
            page: this.currentPage,
            runner_id: this.runnerId
          })
        } else {
          // 用户/店家等：按 user_id（可选）+ role_code（可选）过滤
          res = await getTransactions({
            page: this.currentPage,
            user_id: this.userId || undefined,
            role_code: this.roleCode || undefined
          })
        }
        // 响应结构：{ data: { current_page, data: [items], total, ... } }
        // 请求拦截器已返回 response.data，故 res.data 即为分页对象（paginator）
        const paginator = res.data || {}
        this.list = paginator.data || []
        this.total = paginator.total || this.list.length
      } catch (e) {
        this.$message && this.$message.error('加载流水失败')
      }
    },
    changePage(page) {
      this.currentPage = page
      this.loadTransactions()
    },
    typeText(type) {
      const map = { 1: '充值', 2: '消费', 3: '提现', 4: '退款', 5: '冻结', 6: '解冻', 7: '服务费' }
      return map[type] || '未知'
    },
    statusText(status) {
      if (status === 1) return { text: '成功', cls: 'success' }
      if (status === 0) return { text: '待处理', cls: 'pending' }
      if (status === -1) return { text: '失败', cls: 'failed' }
      return { text: '未知', cls: '' }
    }
  }
}
</script>

<style scoped>
.data-table {
  width: 100%;
  border-collapse: collapse;
  background: #fff;
  border-radius: 6px;
  overflow: hidden;
}
.data-table th,
.data-table td {
  padding: 10px 12px;
  text-align: left;
  border-bottom: 1px solid #f0f0f0;
  font-size: 13px;
  color: #333;
  white-space: nowrap;
}
.data-table th {
  background: #fafafa;
  font-weight: 600;
}
.data-table tr:hover td {
  background: #f9faff;
}
.empty {
  text-align: center;
  color: #999;
  padding: 24px 0;
}
.amount.plus {
  color: #2ecc71;
  font-weight: 600;
}
.amount.minus {
  color: #e74c3c;
  font-weight: 600;
}
.muted {
  color: #aaa;
}
.status-tag {
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 12px;
}
.status-tag.success {
  background: #e8f8f0;
  color: #2ecc71;
}
.status-tag.pending {
  background: #eef4ff;
  color: #667eea;
}
.status-tag.failed {
  background: #fdecea;
  color: #e74c3c;
}
.pagination {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 14px;
  margin-top: 14px;
}
.page-btn {
  padding: 6px 14px;
  border: 1px solid #dcdfe6;
  background: #fff;
  border-radius: 4px;
  cursor: pointer;
}
.page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.page-info {
  font-size: 14px;
  color: #666;
}
</style>
